import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, User, ShoppingBasket, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/data/shop";
import { BeeBadge } from "@/components/community/BeeBadge";
import { useCustomer } from "@/lib/customer";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export const Header = ({ basketCount }) => {
  const { customer } = useCustomer();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const onSearch = (e) => {
    e.preventDefault();
    const term = new FormData(e.target).get("q").trim();
    navigate(term ? `/shop?q=${encodeURIComponent(term)}` : "/shop");
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#202A35]/10" data-testid="site-header">
      <div className="bg-[#202A35] text-[#FFF9E8] text-xs text-center py-2 px-4" data-testid="announcement-bar">
        <span className="text-[#FFD23F] font-semibold">Psst!</span> New bits and bobs added regularly. All prices in GBP (£).
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center gap-4 lg:gap-8">
        <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <Logo tagline />
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-xl relative" data-testid="search-form">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#202A35]/60" />
          <input key={q} name="q" defaultValue={q} type="search" placeholder="What are you looking for?" aria-label="Search products" data-testid="search-input"
            className="w-full h-11 pl-11 pr-4 rounded-full bg-white border-2 border-[#202A35]/15 text-sm focus:outline-none focus:border-[#E2A100] transition-colors" />
        </form>
        <div className="ml-auto flex items-center gap-1">
          <Link to="/account" aria-label={customer ? "My Account" : "Log in"} title={customer ? "My Account" : "Log in"} data-testid="account-button" className="p-2.5 rounded-full hover:bg-[#F5F6F7] transition-colors flex items-center gap-1.5">
            <User size={21} /><span className="hidden lg:inline text-sm font-semibold" data-testid="account-button-label">{customer ? "My Account" : "Log in"}</span>
          </Link>
          <Link to="/basket" aria-label="Basket" data-testid="basket-button" className="relative p-2.5 rounded-full hover:bg-[#F5F6F7] transition-colors">
            <ShoppingBasket size={21} />
            <span data-testid="basket-count" className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E2A100] text-[#202A35] text-[10px] font-bold flex items-center justify-center">
              {basketCount}
            </span>
          </Link>
        </div>
      </div>
      <form onSubmit={onSearch} className="md:hidden px-4 pb-3 relative">
        <Search size={17} className="absolute left-8 top-[22px] -translate-y-1/2 text-[#202A35]/50" />
        <input key={q} name="q" defaultValue={q} type="search" placeholder="What are you looking for?" aria-label="Search products" data-testid="mobile-search-input"
          className="w-full h-11 pl-11 pr-4 rounded-full bg-[#F5F6F7] text-sm focus:outline-none focus:ring-1 focus:ring-[#E2A100]" />
      </form>
      <nav className={`${open ? "block" : "hidden"} lg:block border-t border-[#202A35]/10`} data-testid="main-nav">
        <ul className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:gap-8 py-2 lg:py-0">
          {NAV.map(([n, to]) => (
            <li key={n}>
              <Link to={to} onClick={() => setOpen(false)} data-testid={`nav-${slug(n)}`}
                className={`flex items-center gap-1.5 py-3 text-sm font-medium hover:text-[#E2A100] transition-colors ${n === "Clearance" ? "text-[#B87F00]" : ""} ${to === "/community" ? "font-bold" : ""}`}>
                {to === "/community" && <BeeBadge size={20} testId="nav-bee-badge" />}
                {n}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};
